<template>
	<div>
		<div
			class="border-main-400 relative box-border cursor-pointer rounded-xl border"
			@click="onClickUploader"
		>
			<div
				class="icon pointer-events-none mt-12 flex justify-center"
				:class="{ hidden: files.length }"
			>
				<AddPhotoSvg />
			</div>

			<FilePond
				ref="uploader"
				:class="{ 'pointer-events-none': !files.length }"
				:server="uploadServer"
				@processfileprogress="onProgress"
				@processfile="onProcess"
				@addfilestart="onFileStart"
				@warning="onError"
				@error="onError"
				@updatefiles="onUpdateFiles"
				:allow-multiple="maxFiles > 1"
				name="file"
				:label-idle="files.length ? '+ Add More Images' : 'Add Images'"
				:max-files="maxFiles"
				accepted-file-types="image/jpeg, image/jpg, image/bmp, image/webp, image/png"
				:max-file-size="15 * 1024 * 1024"
				labelDecimalSeparator="qew"
				v-model="files"
			/>
		</div>

		<div v-if="countLimitError" class="text-error mt-2 text-center">
			Maximum {{ maxFiles }} files allowed
		</div>
	</div>
</template>

<script setup lang="ts">
	import 'filepond/dist/filepond.min.css'
	import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
	import { computed, onMounted, ref, watch } from 'vue'
	import { FilePondFile, FileStatus } from 'filepond'
	import vueFilePond from 'vue-filepond'
	import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
	import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
	import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
	import AddPhotoSvg from '../VehicleForm/components/icons/AddPhotoSvg.vue'
	import { useConfigStore } from '../../stores/config'

	export type AppUploadFile = {
		name: string
	}
	type AppFilepondFile = FilePondFile & { progress: number }

	// hooks
	const config = useConfigStore()

	// props
	const props = withDefaults(
		defineProps<{ modelValue: AppUploadFile[]; maxFiles?: number; blocked?: boolean }>(),
		{
			modelValue: () => [],
			maxFiles: 5,
			blocked: false,
		},
	)

	// emits
	const emit = defineEmits(['update:modelValue', 'progressChange'])

	// refs
	const uploader = ref()
	const files = ref<AppFilepondFile[]>([])
	const countLimitError = ref(false)

	// constants
	const FilePond = vueFilePond(
		FilePondPluginFileValidateType,
		FilePondPluginFileValidateSize,
		FilePondPluginImagePreview,
	)
	const apiUrl = config.config?.api_url
		? `${config.config?.api_url}/api/widget/temp/images`
		: `${import.meta.env.VITE_APP_API_URL}temp/images`

	const uploadServer = {
		timeout: 40000,
		process: {
			url: apiUrl,
			method: 'POST',
			headers: {
				accept: 'application/json',
			},
			ondata: (formData: FormData) => {
				const file = formData.getAll('file')[1]
				formData.delete('file')
				formData.append('file', file)

				return formData
			},
			onload: (response: string) => {
				try {
					const resp = JSON.parse(`${response}`)
					return resp.name
				} catch (e) {
					console.log(e)
				}
			},
		},
		restore: (
			source: string,
			load: (blob: Blob) => void,
			error: () => void,
			abort: () => void,
		) => {
			if (source.lastIndexOf) {
				const name = source.slice(source.lastIndexOf('/') + 1)
				const url = `${apiUrl}/${name}`
				const headers: Record<string, string> = {}
				fetch(url, { headers })
					.then(res => res.blob())
					.then(load)
					.catch(error)
			}

			return {
				abort: () => {
					abort()
				},
			}
		},
		revert: (source: string, load: () => void, error: () => void) => {
			if (props.blocked) return

			const name = source.slice(source.lastIndexOf('/') + 1)
			const url = `${apiUrl}/${name}`
			const headers: Record<string, string> = {}
			fetch(url, { method: 'DELETE', headers }).then(load).catch(error)
		},
	}

	// lifecycle
	onMounted(() => {
		props.modelValue.forEach(f => uploader.value.addFile(f.name, { type: 'limbo' }))
	})

	watch(files, updatedFiles => {
		emit(
			'update:modelValue',
			updatedFiles
				.filter(f => f.status === FileStatus.PROCESSING_COMPLETE)
				.map(f => ({
					name: f.serverId,
				})),
		)
	})

	// computed
	const filesInProcessing = computed(() =>
		files.value.filter(
			f => f.status === FileStatus.PROCESSING || f.status === FileStatus.PROCESSING_QUEUED,
		),
	)

	const progress = computed(() => {
		return filesInProcessing.value.length
			? filesInProcessing.value.reduce((prev, curr) => {
					return prev + (curr.progress || 0)
			  }, 0) / filesInProcessing.value.length
			: 0
	})
	watch(progress, updatedProgress => {
		emit('progressChange', updatedProgress)
	})

	// methods
	const onClickUploader = () => {
		if (files.value.length) return
		uploader.value.$el.querySelector('input').click()
	}

	const onUpdateFiles = (fls: (FilePondFile & { progress: number })[]) => {
		files.value = fls
	}

	const onProgress = (file: FilePondFile, fileProgress: number) => {
		let updatedFile = files.value.find(f => f.id === file.id)
		if (updatedFile) updatedFile.progress = fileProgress
	}

	const onProcess = (file: FilePondFile) => {
		if (progress.value === 1) {
			triggerFilesChangeDetect()
		}
	}

	const onFileStart = () => {
		if (
			progress.value === 0 &&
			files.value.some(f => f.status === FileStatus.PROCESSING_QUEUED)
		) {
			emit('progressChange', 0.001)
		}

		countLimitError.value = false
	}

	const onError = (e: { body: string }) => {
		if (e.body === 'Max files') countLimitError.value = true

		triggerFilesChangeDetect()
	}

	const cancelUpload = () => {
		files.value
			.filter(f => f.status !== FileStatus.PROCESSING_COMPLETE)
			.forEach(f => f.abortLoad())
	}

	const triggerFilesChangeDetect = () => {
		// manually trigger change check on "files" ref
		setTimeout(() => (files.value = files.value.splice(0)), 800)
	}

	defineExpose({
		cancelUpload,
	})
</script>

<style>
	a.filepond--credits {
		display: none;
	}

	.filepond--root {
		margin-bottom: 30px;
	}

	.filepond--panel-root {
		background-color: transparent;
	}

	.filepond--drop-label {
		cursor: pointer;
	}

	.filepond--drop-label > label {
		margin-top: 5px !important;
		font-size: 22px;
		font-weight: bold;
		color: rgb(var(--color-main-400));
		cursor: pointer;
	}

	.icon path {
		fill: rgb(var(--color-main-400));
	}
</style>
